import { motion } from "framer-motion";
import { Briefcase, ExternalLink } from "lucide-react";
import { feet } from "./constants";

/**
 * OpportunityCard — Layer 1 careers deep-link card.
 *
 * Copy contract (per user spec)
 * -----------------------------
 * We NEVER imply that a business is currently hiring at Layer 1. The label is
 * "CAREERS NEARBY" (neutral), the CTA is "VIEW CAREERS" (not "VIEW JOB"), and
 * no job title is shown even if the source populated one. Layer 2 (real job
 * source) will flip these to "HIRING HERE" + real titles only when the data
 * source confirms specific openings.
 *
 * Motion
 * ------
 * Wrapped in <motion.div layout> so cards slide in on mount and reflow
 * smoothly when the feed reorders — same conveyor-belt behavior as vibe
 * cards. Fade+shrink on exit powers the "cards disappear immediately when
 * OPPORTUNITY mode is turned off" requirement.
 */
export default function OpportunityCard({ card }) {
  if (!card) return null;
  const ft = feet(card.distance_m);
  // Three confidence tiers per user spec:
  //   verified + at_location  -> "HIRING HERE"  (green, real title shown)
  //   verified (city/employer) -> "JOBS NEARBY"  (grey, real title shown)
  //   deep-link only           -> "CAREERS NEARBY" (pink, no title)
  const atLocation = !!card.at_location;
  const isHiringHere = card.verified && atLocation && card.job_title;
  const isJobsNearby = card.verified && !atLocation && card.job_title;
  // Accent color per tier
  const accent = isHiringHere ? "#22C55E" : isJobsNearby ? "#94A3B8" : "#EC4899";
  const borderRgba = isHiringHere
    ? "rgba(34, 197, 94, 0.55)"
    : isJobsNearby
      ? "rgba(148, 163, 184, 0.55)"
      : "rgba(236, 72, 153, 0.55)";
  const glowRgba = isHiringHere
    ? "rgba(34, 197, 94, 0.18)"
    : isJobsNearby
      ? "rgba(148, 163, 184, 0.12)"
      : "rgba(236, 72, 153, 0.18)";
  const pillLabel = isHiringHere
    ? "HIRING HERE"
    : isJobsNearby
      ? "JOBS NEARBY"
      : "CAREERS NEARBY";
  const ctaLabel = card.verified && card.job_title ? "VIEW JOB" : "VIEW CAREERS";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -36, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.96 }}
      transition={{
        type: "spring", stiffness: 380, damping: 32, mass: 0.9,
        opacity: { duration: 0.28 },
      }}
      data-testid={`opportunity-${card.id}`}
    >
      <a
        href={card.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-3xl bg-black/60 p-4 transition relative"
        style={{
          border: `1px solid ${borderRgba}`,
          boxShadow: `0 0 22px ${glowRgba}, inset 0 0 0 1px ${glowRgba}`,
        }}
        data-testid={`opportunity-link-${card.id}`}
      >
        <div className="flex items-center gap-2 text-[10px] font-mono-accent text-zinc-500">
          <Briefcase size={12} style={{ color: accent }} />
          <span className="tracking-widest" style={{ color: accent }}>
            {pillLabel}
          </span>
          {ft != null && <><span>·</span><span>{ft}FT</span></>}
        </div>
        <div className="mt-2 text-white font-bold text-lg leading-tight" data-testid={`opportunity-name-${card.id}`}>
          {card.business_name}
        </div>
        {card.job_title && (isHiringHere || isJobsNearby) && (
          <div className="mt-1 text-zinc-300 text-sm" data-testid={`opportunity-title-${card.id}`}>
            {card.job_title}
          </div>
        )}
        <div className="mt-1 text-zinc-500 text-[11px] font-mono-accent tracking-wider">
          {(card.employer_name && card.employer_name !== card.business_name) && (
            <span data-testid={`opportunity-employer-${card.id}`}>
              {card.employer_name.toUpperCase()} ·{" "}
            </span>
          )}
          <span>{(card.city || "").toUpperCase()}</span>
          {!card.job_title && (
            <span className="text-zinc-500 normal-case tracking-normal"> · Explore career opportunities</span>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <span
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold tracking-wider transition"
            style={{
              backgroundColor: `${accent}22`,
              border: `1px solid ${accent}66`,
              color: accent,
            }}
            data-testid={`opportunity-cta-${card.id}`}
          >
            {ctaLabel} <ExternalLink size={12} />
          </span>
        </div>
      </a>
    </motion.div>
  );
}
